import { ChatComposer, type ChatComposerOpenAction } from '@ui/ChatComposer';
import { ChatComposerMenu } from '@features/ai-chat/ChatComposerMenu';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import { getTooltipBaseStyles } from '@client/style/tooltip.styles';
import { Box, Tooltip, Typography } from '@mui/material';
import { Icon } from '@ui/Icon';
import { useProjectState } from '@client/store/project';
import {
  useSelectedOrganizationName,
  useFullOrganizationByName,
} from '@client/store/organization/organization.hook';
import { useUser } from '@client/hooks/use-user.hooks';
import { getIsAiChatApproved } from '@shared/helpers/organization.helpers';
import { isAdmin } from '@shared/helpers/user.helpers';
import { readStoredAiChatModel } from '@features/ai-chat/ai-chat-model.storage';
import {
  formatChatMessageTime,
  formatChatThreadTimestamp,
  shouldShowChatTimestamp,
} from '@features/ai-chat/ai-chat-timestamp.helpers';
import { useAiChat } from '@features/ai-chat/use-ai-chat.hooks';
import { useAiChatStore, bucketKey } from '@features/ai-chat/ai-chat.store';
import { aiChatApi } from '@features/ai-chat/ai-chat.api';
import {
  AI_CHAT_MESSAGE_MAX_LENGTH,
  AiConversationSummary,
  AiScopeTarget,
  AiToolCall,
} from '@shared/models/ai-chat.model';
import { useSelectedElementId, useSelectedVersion } from '@client/store/ui';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { hasElementSelection } from '@features/proposals/helpers/proposal-selection.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { UNICODE } from '@shared/models/unicode.model';
import { DotLoader } from '@ui/DotLoader';
import { ChatWindow, ChatWindowContent } from '@ui/ChatWindow';
import { AgentChatMessage } from '@features/ai-chat/AgentChatMessage';
import { useBuildingDesign } from '../hooks/design.hooks';
import { useSelectedBuildingId } from '../store/design-store.hooks';

/** Set on the right-panel aside while the docked input is showing. */
export const DESIGN_AGENT_DOCK_HEIGHT_PROPERTY = '--design-agent-dock-height';
const DESIGN_AGENT_PANEL_INPUT_HEIGHT_PROPERTY =
  '--design-agent-panel-input-height';

const formatToolCall = (tc: AiToolCall): string => {
  const words = tc.name.replace(/_/g, ' ');
  const label = words.charAt(0).toUpperCase() + words.slice(1);
  if (!tc.result) return `${label}…`;
  return tc.result.success
    ? `${label} ✓`
    : `${label} ✗ ${tc.result.error ?? ''}`;
};

const collateToolCalls = (toolCalls: AiToolCall[]): string => {
  const counts = new Map<string, number>();
  for (const tc of toolCalls) {
    const key = formatToolCall(tc);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, n]) =>
      n > 1 ? `${n} ${UNICODE.MATH.MULTIPLICATION} ${label}` : label,
    )
    .join(', ');
};

type UploadClickHandler = () => void;
type UploadFilesHandler = (event: ChangeEvent<HTMLInputElement>) => void;

type AttachedChatFile = {
  id: string;
  file: File;
};

type ChatAttachmentRowProps = {
  files: readonly AttachedChatFile[];
  disabled: boolean;
  onUploadClick: UploadClickHandler;
};

const useStyles = makeStyles<void, 'messageHoverTime'>()(
  (theme, _params, classes) => ({
    panel: {
      position: 'relative',
      display: 'flex',
      flex: '1 1 0',
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
    },
    content: {
      paddingTop: 8,
      paddingBottom: `var(${DESIGN_AGENT_PANEL_INPUT_HEIGHT_PROPERTY}, 12px)`,
      display: 'flex',
      flexDirection: 'column',
    },
    messageTimestamp: {
      '&.MuiTypography-root': {
        margin: 0,
      },
      paddingTop: 14,
      paddingBottom: 2,
      fontSize: 12,
      lineHeight: '16px',
      fontWeight: 400,
      letterSpacing: '0.2px',
      color: 'rgba(0,0,0,0.45)',
      textAlign: 'center',
    },
    messageHoverTime: {
      position: 'absolute',
      margin: 0,
      opacity: 0,
      pointerEvents: 'none',
      '&.MuiTypography-root': {
        margin: 0,
      },
      fontSize: 11,
      lineHeight: '12px',
      fontWeight: 400,
      letterSpacing: '0.2px',
      color: 'rgba(0,0,0,0.45)',
    },
    userMessageHoverTime: {
      top: 'calc(100% - 4px)',
      right: 16,
    },
    userMessageRow: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'flex-end',
      paddingLeft: 36,
      paddingRight: 16,
      paddingTop: 8,
      paddingBottom: 8,
      flexShrink: 0,
      [`&:hover .${classes.messageHoverTime}`]: {
        opacity: 1,
      },
    },
    userBubble: {
      ...getTooltipBaseStyles(theme),
      backgroundColor: 'rgba(40,42,49,0.8)',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      maxWidth: '100%',
      borderRadius: '12px 12px 2px 12px',
      boxShadow: 'none',
    },
    userAttachment: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      minWidth: 0,
    },
    userAttachmentName: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: 12,
      lineHeight: '16px',
    },
    userText: {
      '&.MuiTypography-root': {
        margin: 0,
        color: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        letterSpacing: 'inherit',
        lineHeight: 'inherit',
      },
      overflowWrap: 'anywhere',
    },
    toolCallText: {
      fontSize: 11,
      color: 'rgba(0,0,0,0.5)',
      letterSpacing: '0.44px',
    },
    panelFooter: {
      position: 'absolute',
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      padding: '6px 10px 10px',
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
    uploadButtonAnchor: {
      position: 'absolute',
      bottom: 6,
      left: 6,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    },
    attachedFile: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 20,
      height: 20,
      color: 'rgba(0,0,0,0.55)',
    },
    uploadButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 20,
      height: 20,
      margin: 0,
      padding: 0,
      appearance: 'none',
      border: 'none',
      borderRadius: 5,
      backgroundColor: 'transparent',
      color: 'rgba(0,0,0,0.55)',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.05)',
        color: 'rgba(0,0,0,0.85)',
      },
      '&:disabled': {
        cursor: 'default',
        opacity: 0.4,
      },
    },
    uploadInput: {
      display: 'none',
    },
    emptyText: {
      '&.MuiTypography-root': {
        margin: 0,
      },
      flex: '1 0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 32px',
      fontSize: 12,
      lineHeight: '16px',
      fontWeight: 400,
      letterSpacing: '0.2px',
      color: 'rgba(0,0,0,0.45)',
      textAlign: 'center',
    },
    collapsedWrapper: {
      marginTop: 'auto',
      position: 'sticky',
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 4,
      padding: '6px 10px 10px',
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
    floatingWrapper: {
      marginTop: 0,
      position: 'static',
      padding: 0,
    },
    tooLongText: {
      fontSize: 11,
      color: '#d32f2f',
      letterSpacing: '0.44px',
      padding: '4px 8px 0',
    },
  }),
);

const ChatAttachmentRow: React.FC<ChatAttachmentRowProps> = ({
  files,
  disabled,
  onUploadClick,
}) => {
  const { classes } = useStyles();

  return (
    <span className={classes.uploadButtonAnchor}>
      {files.map((attached) => (
        <Tooltip
          key={attached.id}
          title={attached.file.name}
          placement="top"
          disableInteractive
        >
          <span className={classes.attachedFile}>
            <Icon category="mui" icon="InsertDriveFileOutlined" size={13} />
          </span>
        </Tooltip>
      ))}
      <Tooltip title="Upload files" placement="top" disableInteractive>
        <span>
          <Box
            component="button"
            type="button"
            className={classes.uploadButton}
            aria-label="Upload files"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onUploadClick}
          >
            <Icon category="mui" icon="Add" size={13} />
          </Box>
        </span>
      </Tooltip>
    </span>
  );
};

const hasConversationContent = (item: AiConversationSummary) =>
  item.preview.trim().length > 0;

type DesignAiChatPlacement = 'dock' | 'panel';

/** Tool scope the chat sends each turn with: Design view or Build (elements). */
export type DesignAiChatScope = 'design' | 'elements';

type MessageSentHandler = () => void;

type DesignAiChatTestPanelProps = {
  placement?: DesignAiChatPlacement;
  onMessageSent?: MessageSentHandler;
  onOpenConversation?: (action?: ChatComposerOpenAction) => void;
  openingAction?: ChatComposerOpenAction;
  /** Collapsed composer, positioned by the parent. Used when the right panel is closed. */
  floating?: boolean;
  scope?: DesignAiChatScope;
};

export const DesignAiChatTestPanel: React.FC<DesignAiChatTestPanelProps> = ({
  placement = 'dock',
  onMessageSent,
  onOpenConversation,
  openingAction,
  floating = false,
  scope = 'design',
}) => {
  const organization = useSelectedOrganizationName();
  const fullOrganization = useFullOrganizationByName(organization);
  if (!organization || !getIsAiChatApproved(fullOrganization)) {
    // The Agent tab stays visible, so say why it is empty instead of a blank panel.
    return placement === 'panel' ? <AgentNotTurnedOn /> : null;
  }
  return (
    <DesignAiChatContent
      organization={organization}
      placement={placement}
      onMessageSent={onMessageSent}
      onOpenConversation={onOpenConversation}
      openingAction={openingAction}
      floating={floating}
      scope={scope}
    />
  );
};

const AgentNotTurnedOn: React.FC = () => {
  const { classes } = useStyles();
  return (
    <Box className={classes.panel}>
      <Typography className={classes.emptyText} component="p">
        The agent is not turned on for this organization.
      </Typography>
    </Box>
  );
};

type ChatScopeTarget =
  | { scopeTarget: AiScopeTarget; unavailableReason?: undefined }
  | { scopeTarget?: undefined; unavailableReason: string };

/**
 * Scope target for the next turn, or why the chat cannot send yet.
 * Design needs a building on the project; elements needs a selected version.
 */
const useChatScopeTarget = (scope: DesignAiChatScope): ChatScopeTarget => {
  const { project } = useProjectState('project');
  const design = useBuildingDesign();
  const selectedBuildingId = useSelectedBuildingId();
  const selectedVersion = useSelectedVersion();
  const selectedProposalId = useSelectedProposalId();
  const selectedElementId = useSelectedElementId();
  const hasDesign = guards.is.defined(project.design);
  const buildingIndex = Math.max(
    0,
    design.buildings.findIndex(
      (building) => building.id === selectedBuildingId,
    ),
  );
  const versionId = selectedVersion?.id;
  const elementId = hasElementSelection(selectedElementId)
    ? selectedElementId
    : undefined;

  return useMemo<ChatScopeTarget>(() => {
    switch (scope) {
      case 'design':
        return hasDesign
          ? { scopeTarget: { scope: 'design', buildingIndex } }
          : { unavailableReason: 'No building on project.' };
      case 'elements':
        return versionId
          ? {
              scopeTarget: {
                scope: 'elements',
                versionId,
                proposalId: selectedProposalId,
                elementId,
              },
            }
          : { unavailableReason: 'No version selected.' };
      default:
        return assertUnreachable(scope);
    }
  }, [
    scope,
    hasDesign,
    buildingIndex,
    versionId,
    selectedProposalId,
    elementId,
  ]);
};

const DesignAiChatContent: React.FC<{
  organization: string;
  placement: DesignAiChatPlacement;
  onMessageSent?: MessageSentHandler;
  onOpenConversation?: (action?: ChatComposerOpenAction) => void;
  openingAction?: ChatComposerOpenAction;
  floating?: boolean;
  scope: DesignAiChatScope;
}> = ({
  organization,
  placement,
  onMessageSent,
  onOpenConversation,
  openingAction,
  floating = false,
  scope,
}) => {
  const { classes, cx } = useStyles();
  const { project } = useProjectState('project');
  const user = useUser();
  const canPickModel = isAdmin(user);
  const [draft, setDraft] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedChatFile[]>([]);
  const collapsedDockRef = useRef<HTMLDivElement>(null);
  const panelFooterRef = useRef<HTMLDivElement>(null);

  const { scopeTarget, unavailableReason } = useChatScopeTarget(scope);
  const hasScope = guards.is.defined(scopeTarget);

  const conversationHistory = useAiChatStore(
    (s) => s.conversationListByKey[bucketKey(organization, project.id)],
  );
  const history = conversationHistory?.data.filter(hasConversationContent);
  const historyLoading = conversationHistory?.loading ?? false;

  useEffect(() => {
    useAiChatStore.getState().clearConversationListCache();
  }, [project.id]);

  const chat = useAiChat(organization, project.id);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const overlayInputRef = useRef<HTMLInputElement>(null);
  const skipNextDraftRestoreRef = useRef(false);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const conversation = chat.status === 'ready' ? chat.conversation : undefined;
  const convIdRef = useRef(conversation?.id);
  convIdRef.current = conversation?.id;
  const messages = useMemo(() => conversation?.messages ?? [], [conversation]);
  const messagesScrollKey = useMemo(
    () =>
      messages.map((m) => `${m.id}:${m.content.length}:${m.status}`).join('|'),
    [messages],
  );

  useLayoutEffect(() => {
    const root = messagesContainerRef.current;
    if (!root) return;
    root.scrollTop = root.scrollHeight;
  }, [messagesScrollKey]);

  const saveDraft = useCallback((convId: string | undefined, text: string) => {
    if (!convId) return;
    const key = `nodon:ai-chat:draft:${convId}`;
    if (text.trim()) {
      localStorage.setItem(key, text);
    } else {
      localStorage.removeItem(key);
    }
  }, []);

  // Restore draft when active conversation changes
  useLayoutEffect(() => {
    const curId = conversation?.id;
    if (skipNextDraftRestoreRef.current) {
      skipNextDraftRestoreRef.current = false;
      return;
    }
    const stored = curId
      ? (localStorage.getItem(`nodon:ai-chat:draft:${curId}`) ?? '')
      : '';
    setDraft(stored);
    setAttachedFiles([]);
  }, [conversation?.id]);

  // Save draft on unmount
  useLayoutEffect(
    () => () => saveDraft(convIdRef.current, draftRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on unmount
    [],
  );

  const isSending = chat.status === 'ready' && chat.isSending;
  const waitingForReply =
    chat.status === 'ready' &&
    (chat.isSending || chat.conversation.status === 'processing') &&
    !messages.some(
      (message) =>
        message.role === 'assistant' && message.status !== 'processed',
    );

  useLayoutEffect(() => {
    if (placement !== 'panel') return;
    if (chat.status !== 'ready' || !hasScope) return;
    const el = overlayInputRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
  }, [placement, chat.status, hasScope]);

  // Use the trimmed length so the counter matches what onSend will actually
  // submit — leading/trailing whitespace shouldn't trip the limit.
  const trimmedDraftLength = draft.trim().length;
  const isTooLong = trimmedDraftLength > AI_CHAT_MESSAGE_MAX_LENGTH;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const canSend =
    hasScope &&
    chat.status === 'ready' &&
    !isSending &&
    !isTooLong &&
    (draft.trim().length > 0 || attachedFiles.length > 0);
  const uploadDisabled = isSending || chat.status !== 'ready' || !hasScope;

  const onUploadClick = useCallback<UploadClickHandler>(() => {
    if (placement === 'dock' && onOpenConversation) {
      onOpenConversation('upload');
      return;
    }
    fileInputRef.current?.click();
  }, [placement, onOpenConversation]);

  const onUploadFiles = useCallback<UploadFilesHandler>((event) => {
    // Copy before resetting: `files` is live and empties when `value` is cleared.
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (selected.length === 0) return;
    const next = selected.map((file) => ({
      id: crypto.randomUUID(),
      file,
    }));
    setAttachedFiles((current) => [...current, ...next]);
  }, []);

  const onSend = useCallback(async () => {
    if (chat.status !== 'ready') return;
    const text = draft.trim();
    const files = attachedFiles.map((attached) => attached.file);
    if ((!text && files.length === 0) || !scopeTarget || chat.isSending) return;
    if (text.length > AI_CHAT_MESSAGE_MAX_LENGTH) return;
    setDraft('');
    draftRef.current = '';
    setAttachedFiles([]);
    if (placement === 'dock') onMessageSent?.();
    // Server rejects model overrides from non-admin users; only forward
    // one when the picker is actually available to this user.
    await chat.sendMessage(
      text,
      scopeTarget,
      canPickModel ? readStoredAiChatModel() : undefined,
      files.length > 0 ? files : undefined,
    );
    overlayInputRef.current?.focus();
  }, [
    draft,
    attachedFiles,
    chat,
    canPickModel,
    scopeTarget,
    placement,
    onMessageSent,
  ]);

  const onDraftKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      e.preventDefault();
      void onSend();
    },
    [onSend],
  );

  const onNewChat = useCallback(async () => {
    if (chat.status !== 'ready') return;
    saveDraft(conversation?.id, draft);
    skipNextDraftRestoreRef.current = true;
    setDraft('');
    setAttachedFiles([]);
    await chat.createNewConversation();
    overlayInputRef.current?.focus();
  }, [chat, conversation?.id, draft, saveDraft]);

  const onFetchHistory = useCallback(() => {
    void useAiChatStore
      .getState()
      .fetchConversationList(organization, project.id);
  }, [organization, project.id]);

  useEffect(() => {
    if (placement !== 'panel') return;
    if (openingAction === 'upload') fileInputRef.current?.click();
    if (openingAction === 'menu') void onFetchHistory();
  }, [placement, openingAction, onFetchHistory]);

  const onSelectHistory = useCallback(
    async (id: string) => {
      if (chat.status !== 'ready') return;
      saveDraft(conversation?.id, draft);
      const conv = await aiChatApi.getConversation(id);
      if (conv) chat.switchConversation(conv);
    },
    [chat, conversation?.id, draft, saveDraft],
  );

  useLayoutEffect(() => {
    const dock = collapsedDockRef.current;
    if (!dock) return;
    const panel = dock.closest('aside');
    if (!(panel instanceof HTMLElement)) return;

    const syncHeight = () => {
      panel.style.setProperty(
        DESIGN_AGENT_DOCK_HEIGHT_PROPERTY,
        `${dock.offsetHeight}px`,
      );
    };
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(dock);
    return () => {
      observer.disconnect();
      panel.style.removeProperty(DESIGN_AGENT_DOCK_HEIGHT_PROPERTY);
    };
  }, [placement]);

  useLayoutEffect(() => {
    if (placement !== 'panel') return;
    const footer = panelFooterRef.current;
    const frame = footer?.parentElement;
    if (!footer || !(frame instanceof HTMLElement)) return;

    const syncHeight = () => {
      frame.style.setProperty(
        DESIGN_AGENT_PANEL_INPUT_HEIGHT_PROPERTY,
        `${footer.offsetHeight}px`,
      );
    };
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(footer);
    return () => {
      observer.disconnect();
      frame.style.removeProperty(DESIGN_AGENT_PANEL_INPUT_HEIGHT_PROPERTY);
    };
  }, [placement]);

  if (placement === 'dock') {
    return (
      <Box
        ref={collapsedDockRef}
        className={cx(
          classes.collapsedWrapper,
          floating && classes.floatingWrapper,
        )}
      >
        {isTooLong && (
          <Typography className={classes.tooLongText}>
            Message too long ({trimmedDraftLength.toLocaleString()} /{' '}
            {AI_CHAT_MESSAGE_MAX_LENGTH.toLocaleString()} characters)
          </Typography>
        )}
        {chat.status === 'ready' && chat.sendError && (
          <Typography className={classes.tooLongText}>
            {chat.sendError.message}
          </Typography>
        )}
        <ChatComposer
          appearance="floating"
          onOpenConversation={onOpenConversation}
          value={draft}
          onChange={setDraft}
          onKeyDown={onDraftKeyDown}
          placeholder="Ask the agent..."
          disabled={isSending || chat.status !== 'ready' || !hasScope}
          canSend={canSend}
          onSend={() => void onSend()}
          uploadControl={
            <ChatAttachmentRow
              files={attachedFiles}
              disabled={uploadDisabled}
              onUploadClick={onUploadClick}
            />
          }
          menu={
            <ChatComposerMenu
              history={history}
              historyLoading={historyLoading}
              activeConversationId={conversation?.id}
              newChatDisabled={chat.status !== 'ready' || !conversation?.id}
              onFetchHistory={onFetchHistory}
              onSelectHistory={onSelectHistory}
              onNewChat={() => void onNewChat()}
            />
          }
        >
          <input
            ref={fileInputRef}
            className={classes.uploadInput}
            type="file"
            multiple
            onChange={onUploadFiles}
          />
        </ChatComposer>
      </Box>
    );
  }

  return (
    <ChatWindow variant="inline" className={classes.panel}>
      <ChatWindowContent
        scrollRef={messagesContainerRef}
        className={classes.content}
      >
        {messages.map((m, index) => {
          const pending =
            m.toolCalls?.some((tc) => !tc.result) || m.status !== 'processed';
          const toolSummary = m.toolCalls?.length
            ? collateToolCalls(m.toolCalls)
            : undefined;
          const previousCreatedAt = messages[index - 1]?.createdAt;
          const timestamp = shouldShowChatTimestamp(
            m.createdAt,
            previousCreatedAt,
          )
            ? formatChatThreadTimestamp(m.createdAt, new Date())
            : undefined;

          const isToolLine = !m.content.trim() && Boolean(toolSummary);
          const hoverTime = formatChatMessageTime(m.createdAt);

          if (m.role === 'user') {
            return (
              <Fragment key={m.id}>
                {timestamp && (
                  <Typography
                    className={classes.messageTimestamp}
                    component="p"
                  >
                    {timestamp}
                  </Typography>
                )}
                <Box className={classes.userMessageRow}>
                  <Box className={classes.userBubble}>
                    {m.attachments?.map((attachment) => (
                      <span
                        key={attachment.fileId}
                        className={classes.userAttachment}
                      >
                        <Icon
                          category="mui"
                          icon="InsertDriveFileOutlined"
                          size={13}
                        />
                        <span className={classes.userAttachmentName}>
                          {attachment.name}
                        </span>
                      </span>
                    ))}
                    {(m.content || pending) && (
                      <Typography className={classes.userText} component="p">
                        {m.content}
                        {pending && <DotLoader />}
                      </Typography>
                    )}
                  </Box>
                  {hoverTime && (
                    <Typography
                      className={cx(
                        classes.messageHoverTime,
                        classes.userMessageHoverTime,
                      )}
                      component="p"
                    >
                      {hoverTime}
                    </Typography>
                  )}
                </Box>
              </Fragment>
            );
          }

          return (
            <AgentChatMessage
              key={m.id}
              message={m}
              timestamp={timestamp}
              toolLine={isToolLine}
              trailing={
                (toolSummary || pending) && (
                  <>
                    {toolSummary && (
                      <Typography
                        component="span"
                        className={classes.toolCallText}
                      >
                        {' '}
                        {toolSummary}
                      </Typography>
                    )}
                    {pending && <DotLoader />}
                  </>
                )
              }
            />
          );
        })}
        {waitingForReply && (
          <AgentChatMessage
            message={{ content: '', createdAt: '' }}
            trailing={
              <span role="status" aria-label="Thinking">
                <DotLoader />
              </span>
            }
          />
        )}
        {!messages.length && !waitingForReply && (
          <Typography className={classes.emptyText} component="p">
            {chat.status === 'error'
              ? `Error: ${chat.error.message}`
              : chat.status !== 'ready'
                ? 'Loading…'
                : (unavailableReason ??
                  'Tell the agent what you need help with.')}
          </Typography>
        )}
      </ChatWindowContent>

      <Box
        ref={placement === 'panel' ? panelFooterRef : undefined}
        className={classes.panelFooter}
      >
        {isTooLong && (
          <Typography className={classes.tooLongText}>
            Message too long ({trimmedDraftLength.toLocaleString()} /{' '}
            {AI_CHAT_MESSAGE_MAX_LENGTH.toLocaleString()} characters)
          </Typography>
        )}
        {chat.status === 'ready' && chat.sendError && (
          <Typography className={classes.tooLongText}>
            {chat.sendError.message}
          </Typography>
        )}
        <ChatComposer
          inputRef={overlayInputRef}
          appearance="panel"
          value={draft}
          onChange={setDraft}
          onKeyDown={onDraftKeyDown}
          placeholder="Do anything"
          disabled={isSending || chat.status !== 'ready' || !hasScope}
          canSend={canSend}
          onSend={() => void onSend()}
          uploadControl={
            <ChatAttachmentRow
              files={attachedFiles}
              disabled={uploadDisabled}
              onUploadClick={onUploadClick}
            />
          }
          menu={
            <ChatComposerMenu
              initiallyOpen={openingAction === 'menu'}
              history={history}
              historyLoading={historyLoading}
              activeConversationId={conversation?.id}
              newChatDisabled={chat.status !== 'ready' || !conversation?.id}
              onFetchHistory={onFetchHistory}
              onSelectHistory={onSelectHistory}
              onNewChat={() => void onNewChat()}
            />
          }
        >
          <input
            ref={fileInputRef}
            className={classes.uploadInput}
            type="file"
            multiple
            onChange={onUploadFiles}
          />
        </ChatComposer>
      </Box>
    </ChatWindow>
  );
};
